import React, { useCallback, useMemo } from 'react';
import { useGoodsBasic } from '../../queries/useGoodsBasic';
import {
  Avatar,
  Divider,
  Flex,
  HorizontalSpacing,
  IconButton,
  IconHeart,
  IconHeartFilled,
  Spinner,
  Typography,
  VerticalSpacing,
} from '@banhala/ui-kit';
import { useGoodsDetail } from '../../queries/useGoodsDetail';
import { useGoodsRoute } from '../../../../hooks/useGoodsRoute';
import { formatNumber, isNotNil, useNavigator } from 'shared/src/utils';
import { useIsFavoriteMarket } from '../../hooks/useIsFavoriteMarket';
import { useOnPressFavoriteMarket } from '../../hooks/useOnPressFavoriteMarket';

export const MarketInfo = React.memo(() => {
  const { data: goodsBasic } = useGoodsBasic();
  const { data: goodsDetail } = useGoodsDetail();
  const { params } = useGoodsRoute<'GoodsDetail'>();

  const { isFavoriteMarket } = useIsFavoriteMarket();

  const { action, loading } = useOnPressFavoriteMarket({
    sno: goodsDetail?.goods.market.sno,
    favorite: isFavoriteMarket,
  });

  const marketTrophies = useMemo(
    () => goodsDetail?.goods.market.trophies ?? [],
    [goodsDetail?.goods.market.trophies],
  );

  const marketLikeCountFormat = useMemo(
    () => formatNumber(goodsDetail?.goods.market.score.favorites_count ?? 0),
    [goodsDetail?.goods.market.score.favorites_count],
  );
  const { navigator } = useNavigator();

  const onPressRow = useCallback(() => {
    if (isNotNil(goodsBasic)) {
      navigator.moveToMarketHome({
        sno: goodsBasic.market.sno,
        isDeeplink: false,
      });
    }
  }, [goodsBasic, navigator]);

  return (
    <Flex>
      <Flex.Row
        alignItems={'center'}
        padding={{ vertical: 12, horizontal: 16 }}
        onPress={onPressRow}
      >
        {isNotNil(goodsBasic?.market.image) ? (
          <Avatar uri={goodsBasic?.market.image} size={36} />
        ) : (
          <Flex
            width={36}
            height={36}
            borderRadius={18}
            borderWidth={1}
            borderColor={'border_secondary'}
          />
        )}
        <HorizontalSpacing size={12} />
        <Flex flex={1} justifyContent={'center'}>
          <Typography type={'subtitle3'}>
            {goodsBasic?.market.name ??
              (typeof params.extras !== 'string'
                ? params.extras?.market_name
                : undefined) ??
              ''}
          </Typography>
          <VerticalSpacing size={6} />
          <Flex.Row>
            {marketTrophies.length > 0 ? (
              marketTrophies.map(trophies => (
                <Typography type={'subtitle3'} key={trophies.title}>
                  {trophies.title}
                </Typography>
              ))
            ) : (
              <Typography type={'body4'} color={'content_secondary'}>
                {goodsBasic?.market.style_tags.join(' ')}
              </Typography>
            )}
            {isNotNil(
              goodsDetail?.goods.market.purchasing_satisfaction_ratio,
            ) && (
              <Typography type={'body4'} color={'content_secondary'}>
                <Typography type={'body3'} color={'content_secondary'}>
                  {' · '}
                </Typography>
                마켓 만족도{' '}
                {Math.ceil(
                  goodsDetail?.goods.market.purchasing_satisfaction_ratio,
                )}
                %
              </Typography>
            )}
          </Flex.Row>
        </Flex>
        <HorizontalSpacing size={12} />
        <IconButton
          size={'small'}
          onPress={() => {
            if (loading) return;

            action();
          }}
          label={`${marketLikeCountFormat.value}${marketLikeCountFormat.label}`}
        >
          {loading ? (
            <Spinner size={20} color={'content_pink_primary'} />
          ) : isFavoriteMarket ? (
            <IconHeartFilled />
          ) : (
            <IconHeart />
          )}
        </IconButton>
      </Flex.Row>
      <Divider size={'medium'} />
    </Flex>
  );
});
